import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Statistic,
  Row,
  Col,
  theme,
  Descriptions,
  Tabs,
  Spin,
  Empty,
  message,
  Modal,
  Input,
  Alert,
  Calendar,
  Badge,
  DatePicker,
} from "antd";
import {
  ArrowLeftOutlined,
  ControlOutlined,
  ThunderboltOutlined,
  BankOutlined,
  DollarOutlined,
  HistoryOutlined,
  ScheduleOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const AEST_TIMEZONE = "Australia/Sydney";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const { Title, Text } = Typography;

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

// Summary stat card component
function SummaryStatCard({ title, value, suffix, prefix, icon, color, loading }) {
  const { token } = theme.useToken();

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>{title}</Text>
          <Statistic
            value={loading ? "—" : value}
            suffix={suffix}
            prefix={prefix}
            valueStyle={{ fontSize: 24, fontWeight: 700, color: token.colorText }}
            loading={loading}
          />
        </div>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: color || "rgba(84, 162, 204, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color: token.colorPrimary,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function ControlGroupDetail() {
  const { token } = theme.useToken();
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [groupInfo, setGroupInfo] = useState(null);
  const [systems, setSystems] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [revenueTotal, setRevenueTotal] = useState(0);
  const [futureDispatches, setFutureDispatches] = useState([]);
  const [systemsPageSize, setSystemsPageSize] = useState(10);
  const [dispatchPageSize, setDispatchPageSize] = useState(10);
  const [scheduledPageSize, setScheduledPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("overview");

  // Realtime chart state
  const [realtimeData, setRealtimeData] = useState([]);
  const [realtimeLatest, setRealtimeLatest] = useState(null);
  const [realtimeLoading, setRealtimeLoading] = useState(true);
  const [realtimeError, setRealtimeError] = useState(null); // null = no error, string = error message

  // Emergency Stop state
  const [emergencyStopModalVisible, setEmergencyStopModalVisible] = useState(false);
  const [emergencyStopReason, setEmergencyStopReason] = useState("");
  const [emergencyStopLoading, setEmergencyStopLoading] = useState(false);
  const [isEmergencyStopped, setIsEmergencyStopped] = useState(false);

  // Aggregation Snapshot state
  const [snapshotCalendarMonth, setSnapshotCalendarMonth] = useState(dayjs());
  const [snapshotDatesWithData, setSnapshotDatesWithData] = useState([]);
  const [selectedSnapshotDate, setSelectedSnapshotDate] = useState(null);
  const [selectedSnapshotData, setSelectedSnapshotData] = useState(null);
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotHistoryTab, setSnapshotHistoryTab] = useState("energy");

  const fetchGroupDetail = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${authToken}` };

      const response = await fetch(`/api/control-groups/${groupId}`, { headers });
      const data = await response.json();

      if (data.success && data.data) {
        setGroupInfo(data.data.group);
        setSystems(data.data.systems || []);
        setDispatches(data.data.dispatches || []);
        setRevenueTotal(data.data.revenueTotal || 0);
        setFutureDispatches(data.data.futureDispatches || []);
      } else {
        message.error(data.message || "Failed to load control group details");
      }
    } catch (error) {
      console.error("Failed to fetch group detail:", error);
      message.error("Failed to load control group details");
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      fetchGroupDetail();
    }
  }, [groupId, fetchGroupDetail]);

  // Fetch realtime chart data
  const fetchRealtimeData = useCallback(async () => {
    setRealtimeLoading(true);
    setRealtimeError(null);
    try {
      const authToken = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${authToken}` };

      const response = await fetch(`/api/control-groups/${groupId}/realtime`, { headers });
      const data = await response.json();

      if (!response.ok || !data.success) {
        // Server error (likely Cassandra connection issue)
        const errorMsg = data.message || `Server error (${response.status})`;
        console.error("Realtime API error:", errorMsg);
        setRealtimeError(errorMsg.includes("Cassandra") || errorMsg.includes("timeout") || errorMsg.includes("connection")
          ? "Database connection unavailable. Realtime data requires access to the time-series database."
          : errorMsg);
        setRealtimeData([]);
        setRealtimeLatest(null);
        return;
      }

      if (data.data) {
        // Transform data for chart - format time in AEST
        const chartData = (data.data.chartData || []).map((point) => ({
          label: dayjs(point.ts).tz(AEST_TIMEZONE).format("HH:mm"),
          ts: point.ts,
          pv: point.pv,
          load: point.load,
          battery: point.battery,
          headroom: point.headroom,
          footroom: point.footroom,
        }));
        setRealtimeData(chartData);
        setRealtimeLatest(data.data.latest);

        // If we got an empty result, this is "no data" not an error
        if (chartData.length === 0 || !chartData.some(d => d.pv !== null)) {
          setRealtimeError(null); // Clear error - this is expected "no data" state
        }
      }
    } catch (error) {
      console.error("Failed to fetch realtime data:", error);
      setRealtimeError("Network error. Please check your connection.");
      setRealtimeData([]);
      setRealtimeLatest(null);
    } finally {
      setRealtimeLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      fetchRealtimeData();
      // Refresh every 60 seconds
      const interval = setInterval(fetchRealtimeData, 60000);
      return () => clearInterval(interval);
    }
  }, [groupId, fetchRealtimeData]);

  // Fetch snapshot calendar data
  const fetchSnapshotCalendar = useCallback(async (year, month) => {
    try {
      const authToken = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await fetch(
        `/api/control-groups/${groupId}/snapshots/calendar?year=${year}&month=${month}`,
        { headers }
      );
      const data = await response.json();
      if (data.success) {
        setSnapshotDatesWithData(data.data.datesWithData || []);
      }
    } catch (error) {
      console.error("Failed to fetch snapshot calendar:", error);
    }
  }, [groupId]);

  // Fetch snapshot for a specific date
  const fetchSnapshotDay = useCallback(async (date) => {
    setSnapshotLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await fetch(
        `/api/control-groups/${groupId}/snapshots/${date}`,
        { headers }
      );
      const data = await response.json();
      if (data.success && data.data) {
        setSelectedSnapshotData(data.data);
      } else {
        setSelectedSnapshotData(null);
      }
    } catch (error) {
      console.error("Failed to fetch snapshot day:", error);
      setSelectedSnapshotData(null);
    } finally {
      setSnapshotLoading(false);
    }
  }, [groupId]);

  // Fetch snapshot history for chart
  const fetchSnapshotHistory = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${authToken}` };
      const endDate = dayjs().format("YYYY-MM-DD");
      const startDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
      const response = await fetch(
        `/api/control-groups/${groupId}/snapshots/history?startDate=${startDate}&endDate=${endDate}`,
        { headers }
      );
      const data = await response.json();
      if (data.success) {
        setSnapshotHistory(data.data.history || []);
      }
    } catch (error) {
      console.error("Failed to fetch snapshot history:", error);
    }
  }, [groupId]);

  // Load snapshot data on mount
  useEffect(() => {
    if (groupId) {
      fetchSnapshotCalendar(snapshotCalendarMonth.year(), snapshotCalendarMonth.month() + 1);
      fetchSnapshotHistory();
    }
  }, [groupId, fetchSnapshotCalendar, fetchSnapshotHistory, snapshotCalendarMonth]);

  // Handle date selection in calendar
  const handleSnapshotDateSelect = (date) => {
    const dateStr = date.format("YYYY-MM-DD");
    const hasData = snapshotDatesWithData.some((d) => d.date === dateStr);
    if (hasData) {
      setSelectedSnapshotDate(dateStr);
      fetchSnapshotDay(dateStr);
      setSnapshotHistoryTab("power"); // Default to Power tab when selecting a date
    }
  };

  // Emergency Stop handlers
  const handleEmergencyStop = async () => {
    if (!emergencyStopReason.trim()) {
      message.error("Please provide a reason for the emergency stop");
      return;
    }

    setEmergencyStopLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`/api/control-groups/${groupId}/emergency-stop`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: emergencyStopReason,
          stoppedAt: new Date().toISOString(),
        }),
      });

      const data = await response.json();
      if (data.success) {
        message.success("Emergency stop activated. All dispatches halted.");
        setIsEmergencyStopped(true);
        setEmergencyStopModalVisible(false);
        setEmergencyStopReason("");
        fetchGroupDetail(); // Refresh data
      } else {
        message.error(data.message || "Failed to activate emergency stop");
      }
    } catch (error) {
      console.error("Emergency stop error:", error);
      message.error("Failed to activate emergency stop");
    } finally {
      setEmergencyStopLoading(false);
    }
  };

  const handleResumeOperations = async () => {
    Modal.confirm({
      title: "Resume Operations",
      icon: <PlayCircleOutlined style={{ color: "#52c41a" }} />,
      content: "Are you sure you want to resume normal operations for this control group?",
      okText: "Resume",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        try {
          const authToken = localStorage.getItem("token");
          const response = await fetch(`/api/control-groups/${groupId}/resume`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          });

          const data = await response.json();
          if (data.success) {
            message.success("Operations resumed successfully");
            setIsEmergencyStopped(false);
            fetchGroupDetail();
          } else {
            message.error(data.message || "Failed to resume operations");
          }
        } catch (error) {
          console.error("Resume error:", error);
          message.error("Failed to resume operations");
        }
      },
    });
  };

  const getStatusConfig = (status) => {
    const statusConfig = {
      ACTIVE: { color: "success", label: "Active", icon: <CheckCircleOutlined /> },
      RUNNING: { color: "processing", label: "Running", icon: <PlayCircleOutlined /> },
      INACTIVE: { color: "default", label: "Inactive", icon: <ClockCircleOutlined /> },
      EMERGENCY_STOPPED: { color: "error", label: "Emergency Stopped", icon: <StopOutlined /> },
    };
    return statusConfig[status] || { color: "default", label: status, icon: <ClockCircleOutlined /> };
  };

  const systemColumns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      width: 150,
      render: (sn) => <Text code style={{ fontSize: 12 }}>{sn}</Text>,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
      width: 140,
      render: (nmi) => <Text type="secondary" style={{ fontSize: 12 }}>{nmi}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        const config = {
          active: { color: "success", icon: <CheckCircleOutlined /> },
          idle: { color: "default", icon: <ClockCircleOutlined /> },
          offline: { color: "error", icon: <ExclamationCircleOutlined /> },
        };
        const { color, icon } = config[status] || config.idle;
        return <Tag color={color} icon={icon}>{status}</Tag>;
      },
    },
    {
      title: "Inverter",
      dataIndex: "inverterKW",
      key: "inverterKW",
      width: 100,
      align: "right",
      render: (val) => <Text>{val?.toFixed(2)} kW</Text>,
    },
    {
      title: "Battery",
      dataIndex: "batteryKWh",
      key: "batteryKWh",
      width: 100,
      align: "right",
      render: (val) => <Text style={{ color: "#f5a623" }}>{val?.toFixed(2)} kWh</Text>,
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: 150,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 80,
      align: "center",
      render: (state) => <Tag>{state}</Tag>,
    },
  ];

  const dispatchColumns = [
    {
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      key: "dispatchId",
      width: 140,
      render: (id, record) => {
        // Determine the target page based on dispatch status
        const getDispatchPath = () => {
          const status = record.status?.toLowerCase();
          if (status === "running" || status === "booking") {
            return `/dispatch/current/${id}`;
          } else if (status === "scheduled" || status === "pending") {
            return `/dispatch/booked?dispatch=${id}`;
          } else {
            // completed, cancelled, expired, error → report
            return `/dispatch/report?dispatch=${id}`;
          }
        };
        return (
          <Text
            code
            style={{ fontSize: 11, cursor: "pointer", color: token.colorPrimary }}
            onClick={() => navigate(getDispatchPath())}
          >
            {id?.slice(-12) || id}
          </Text>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "dispatchType",
      key: "dispatchType",
      width: 90,
      render: (type) => {
        const sourceConfig = {
          MANUAL: { color: "blue", label: "Manual" },
          SCHEDULE: { color: "cyan", label: "Schedule" },
          PRICE: { color: "gold", label: "Price" },
          FCAS: { color: "purple", label: "FCAS" },
          DNSP: { color: "orange", label: "DNSP" },
          API: { color: "geekblue", label: "API" },
        };
        const config = sourceConfig[type] || { color: "default", label: type };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Command",
      dataIndex: "commandType",
      key: "commandType",
      width: 100,
      render: (type) => {
        if (!type) return <Text type="secondary">—</Text>;
        const cmdConfig = {
          IDLE: { color: "default", label: "Idle" },
          CHARGE: { color: "green", label: "Charge" },
          DISCHARGE: { color: "red", label: "Discharge" },
          RESERVE: { color: "blue", label: "Reserve" },
          FCAS_RAISE: { color: "purple", label: "FCAS Raise" },
          FCAS_LOWER: { color: "magenta", label: "FCAS Lower" },
        };
        const config = cmdConfig[type] || { color: "default", label: type };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Start Time",
      dataIndex: "startTime",
      key: "startTime",
      width: 150,
      render: (time) => time ? dayjs(time).format("DD/MM HH:mm") : "—",
    },
    {
      title: "End Time",
      dataIndex: "endTime",
      key: "endTime",
      width: 150,
      render: (time) => time ? dayjs(time).format("DD/MM HH:mm") : "—",
    },
    {
      title: "Power",
      dataIndex: "powerKw",
      key: "powerKw",
      width: 80,
      align: "right",
      render: (val) => val ? <Text>{val.toFixed(1)} kW</Text> : "—",
    },
    {
      title: "Energy",
      dataIndex: "energyKwh",
      key: "energyKwh",
      width: 90,
      align: "right",
      render: (val) => val ? <Text>{val.toFixed(2)} kWh</Text> : "—",
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      width: 90,
      align: "right",
      render: (val) => val ? <Text style={{ color: "#52c41a" }}>${val.toFixed(2)}</Text> : "—",
    },
    {
      title: "Devices",
      dataIndex: "participatingDevices",
      key: "participatingDevices",
      width: 70,
      align: "center",
      render: (val, record) => {
        if (!val && !record.totalDevices) return "—";
        return <Text type="secondary">{val || 0}/{record.totalDevices || 0}</Text>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status, record) => {
        const config = {
          pending: { color: "default", label: "Pending" },
          scheduled: { color: "warning", label: "Scheduled" },
          booking: { color: "processing", label: "Booking" },
          running: { color: "processing", label: "Running" },
          completed: { color: "success", label: "Completed" },
          cancelled: { color: "error", label: "Cancelled" },
          expired: { color: "default", label: "Expired" },
          error: { color: "error", label: "Error" },
        };
        const { color, label } = config[status] || { color: "default", label: status };
        const completion = record.completionRate != null ? ` (${record.completionRate.toFixed(0)}%)` : "";
        return <Tag color={color}>{label}{completion}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <PageLayout active="controlGroupSummary">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!groupInfo) {
    return (
      <PageLayout active="controlGroupSummary">
        <Empty description="Control group not found">
          <Button type="primary" onClick={() => navigate("/control-group/summary")}>
            Back to Management
          </Button>
        </Empty>
      </PageLayout>
    );
  }

  const statusConfig = getStatusConfig(groupInfo.status);

  const tabItems = [
    {
      key: "overview",
      label: (
        <span>
          <ControlOutlined />
          Overview
        </span>
      ),
      children: (
        <>
          {/* Group Info */}
          <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
            <Descriptions
              title="Group Configuration"
              column={{ xs: 1, sm: 2, lg: 3 }}
              labelStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item label="Name">
                <Space>
                  {groupInfo.name}
                  <Tag color={statusConfig.color} icon={statusConfig.icon}>
                    {statusConfig.label}
                  </Tag>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {groupInfo.description || "No description"}
              </Descriptions.Item>
              <Descriptions.Item label="Dispatch Policy">
                <Tag color="blue">{groupInfo.dispatchPolicy || "Default"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Revenue Rule">
                <Tag color="green">{groupInfo.revenueRule || "Standard"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Allocation Rule">
                <Tag color="purple">{groupInfo.allocationRule || "Standard"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total Systems">{systems.length}</Descriptions.Item>
              <Descriptions.Item label="Total Inverter Capacity">
                {(() => { const f = formatPower(systems.reduce((sum, s) => sum + (s.inverterKW || 0), 0), "kW"); return `${f.value} ${f.suffix}`; })()}
              </Descriptions.Item>
              <Descriptions.Item label="Total Battery Capacity">
                {(() => { const f = formatPower(systems.reduce((sum, s) => sum + (s.batteryKWh || 0), 0), "kWh"); return `${f.value} ${f.suffix}`; })()}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Systems in this group */}
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={
              <Space>
                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Systems in Group</span>
              </Space>
            }
            extra={<Text type="secondary">{systems.length} systems</Text>}
            styles={{
              header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px" },
              body: { padding: 0 },
            }}
          >
            <Table
              size="small"
              columns={systemColumns}
              dataSource={systems}
              rowKey="id"
              pagination={makePagination(systemsPageSize, setSystemsPageSize)}
              scroll={{ x: 900 }}
              locale={{ emptyText: "No systems assigned to this group" }}
            />
          </Card>
        </>
      ),
    },
    {
      key: "dispatches",
      label: (
        <span>
          <HistoryOutlined />
          Historical Dispatch
        </span>
      ),
      children: (
        <Card
          bordered={false}
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <HistoryOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontSize: 16, fontWeight: 600 }}>Dispatch History</span>
            </Space>
          }
          extra={<Text type="secondary">{dispatches.length} records</Text>}
          styles={{
            header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px" },
            body: { padding: 0 },
          }}
        >
          <Table
            size="small"
            columns={dispatchColumns}
            dataSource={dispatches}
            rowKey="dispatchId"
            pagination={makePagination(dispatchPageSize, setDispatchPageSize)}
            scroll={{ x: 1000 }}
            locale={{ emptyText: "No dispatch history found" }}
          />
        </Card>
      ),
    },
    {
      key: "scheduled",
      label: (
        <span>
          <ScheduleOutlined />
          Scheduled Dispatch
        </span>
      ),
      children: (
        <Card
          bordered={false}
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <ScheduleOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontSize: 16, fontWeight: 600 }}>Upcoming Dispatch</span>
            </Space>
          }
          extra={<Text type="secondary">{futureDispatches.length} scheduled</Text>}
          styles={{
            header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px" },
            body: { padding: 0 },
          }}
        >
          <Table
            size="small"
            columns={dispatchColumns.filter(c => !["revenue", "energyKwh", "participatingDevices"].includes(c.key))}
            dataSource={futureDispatches}
            rowKey="dispatchId"
            pagination={makePagination(scheduledPageSize, setScheduledPageSize)}
            scroll={{ x: 900 }}
            locale={{ emptyText: "No scheduled dispatches" }}
          />
        </Card>
      ),
    },
  ];

  return (
    <PageLayout active="controlGroupSummary">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/control-group/summary")}
          style={{ marginBottom: 8, padding: 0 }}
        >
          Back to Management
        </Button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Title level={2} style={{ marginBottom: 0 }}>{groupInfo.name}</Title>
            <Tag color={statusConfig.color} icon={statusConfig.icon} style={{ fontSize: 14, padding: "4px 12px" }}>
              {statusConfig.label}
            </Tag>
            {(isEmergencyStopped || groupInfo.status === "EMERGENCY_STOPPED") && (
              <Tag color="error" icon={<StopOutlined />} style={{ fontSize: 14, padding: "4px 12px" }}>
                Emergency Stopped
              </Tag>
            )}
          </div>
          <Space>
            {(isEmergencyStopped || groupInfo.status === "EMERGENCY_STOPPED") ? (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleResumeOperations}
              >
                Resume Operations
              </Button>
            ) : (
              <Button
                danger
                type="primary"
                icon={<StopOutlined />}
                onClick={() => setEmergencyStopModalVisible(true)}
              >
                Emergency Stop
              </Button>
            )}
          </Space>
        </div>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {groupInfo.description || "No description"}
        </Text>
      </div>

      {/* Emergency Stop Alert */}
      {(isEmergencyStopped || groupInfo.status === "EMERGENCY_STOPPED") && (
        <Alert
          message="Emergency Stop Active"
          description="All dispatch operations for this control group have been halted. This overrides all strategy rules including compliance locks. Only authorized operators can resume operations."
          type="error"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Systems"
            value={systems.length}
            icon={<ThunderboltOutlined />}
            color="rgba(84, 162, 204, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {(() => { const f = formatPower(systems.reduce((sum, s) => sum + (s.inverterKW || 0), 0), "kW"); return (
          <SummaryStatCard
            title="Inverter Capacity"
            value={f.value}
            suffix={f.suffix}
            icon={<ThunderboltOutlined />}
            color="rgba(250, 173, 20, 0.1)"
            loading={loading}
          />); })()}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {(() => { const f = formatPower(systems.reduce((sum, s) => sum + (s.batteryKWh || 0), 0), "kWh"); return (
          <SummaryStatCard
            title="Battery Capacity"
            value={f.value}
            suffix={f.suffix}
            icon={<BankOutlined />}
            color="rgba(245, 166, 35, 0.1)"
            loading={loading}
          />); })()}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Revenue"
            value={revenueTotal.toFixed(2)}
            prefix="$"
            icon={<DollarOutlined />}
            color="rgba(5, 166, 119, 0.1)"
            loading={loading}
          />
        </Col>
      </Row>

      {/* Real-time Status Section */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16 }}
        title={
          <Space>
            <LineChartOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Real-time Status</span>
          </Space>
        }
        extra={
          realtimeLatest && (
            <Space size="middle" wrap>
              <Text type="secondary">
                PV: <Text strong style={{ color: "#faad14" }}>{(realtimeLatest.pvKw || 0).toFixed(1)} kW</Text>
              </Text>
              <Text type="secondary">
                Load: <Text strong style={{ color: token.colorPrimary }}>{(realtimeLatest.loadKw || 0).toFixed(1)} kW</Text>
              </Text>
              <Text type="secondary">
                Battery: <Text strong style={{ color: "#52c41a" }}>{(realtimeLatest.batteryKw || 0).toFixed(1)} kW</Text>
              </Text>
              <Text type="secondary">
                Headroom: <Text strong style={{ color: "#722ed1" }}>{(realtimeLatest.headroomKw || 0).toFixed(1)} kW</Text>
              </Text>
              <Text type="secondary">
                Footroom: <Text strong style={{ color: "#eb2f96" }}>{(realtimeLatest.footroomKw || 0).toFixed(1)} kW</Text>
              </Text>
              <Text type="secondary">
                Online: <Text strong style={{ color: token.colorSuccess }}>{realtimeLatest.onlineCount || 0}</Text>
              </Text>
            </Space>
          )
        }
        styles={{
          header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px" },
          body: { padding: "24px" },
        }}
      >
        {realtimeLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
            <Spin size="large" />
          </div>
        ) : realtimeError ? (
          <Alert
            type="warning"
            showIcon
            message="Unable to load realtime data"
            description={realtimeError}
            style={{ marginBottom: 16 }}
          />
        ) : realtimeData.length === 0 || !realtimeData.some(d => d.pv !== null) ? (
          <Empty
            description={
              <Space direction="vertical" size="small" align="center">
                <Text>No realtime data available for today</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ensure the Realtime Engine is running and processing data for this control group.
                </Text>
              </Space>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={realtimeData} margin={{ top: 10, right: 60, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="pvGradGroup" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#faad14" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#faad14" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="loadGradGroup" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  tickCount={12}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: "kW", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: "Capacity (kW)", angle: 90, position: "insideRight", style: { fontSize: 10 } }} />
                <RechartsTooltip
                  formatter={(value, name) => [value != null ? `${Number(value).toFixed(2)} kW` : "N/A", name]}
                  labelFormatter={(label) => `Time: ${label} (Sydney)`}
                />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="pv" name="PV" stroke="#faad14" fill="url(#pvGradGroup)" connectNulls={false} />
                <Area yAxisId="left" type="monotone" dataKey="load" name="Load" stroke={token.colorPrimary} fill="url(#loadGradGroup)" connectNulls={false} />
                <Area yAxisId="left" type="monotone" dataKey="battery" name="Battery" stroke="#52c41a" fill="none" strokeWidth={2} connectNulls={false} />
                <Line yAxisId="right" type="monotone" dataKey="headroom" name="Headroom" stroke="#722ed1" strokeDasharray="5 5" dot={false} connectNulls={false} />
                <Line yAxisId="right" type="monotone" dataKey="footroom" name="Footroom" stroke="#eb2f96" strokeDasharray="5 5" dot={false} connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Group Aggregation Snapshot Section */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16 }}
        title={
          <Space>
            <HistoryOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Group Aggregation Snapshot</span>
          </Space>
        }
        styles={{
          header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px" },
          body: { padding: "16px" },
        }}
      >
        <Row gutter={24}>
          {/* Left: Month Picker + Calendar + Scorecards (25%) */}
          <Col xs={24} lg={6}>
            {/* Month Picker */}
            <div style={{ marginBottom: 12 }}>
              <DatePicker
                picker="month"
                value={snapshotCalendarMonth}
                onChange={(date) => {
                  if (date) {
                    setSnapshotCalendarMonth(date);
                    fetchSnapshotCalendar(date.year(), date.month() + 1);
                  }
                }}
                style={{ width: "100%" }}
                allowClear={false}
              />
            </div>

            {/* Calendar (date only view) */}
            <div>
              <Calendar
                fullscreen={false}
                value={selectedSnapshotDate ? dayjs(selectedSnapshotDate) : snapshotCalendarMonth}
                headerRender={() => null}
                onSelect={handleSnapshotDateSelect}
                cellRender={(date, info) => {
                  if (info.type !== "date") return null;
                  const dateStr = date.format("YYYY-MM-DD");
                  const hasData = snapshotDatesWithData.some((d) => d.date === dateStr);
                  if (hasData) {
                    return (
                      <div style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: token.colorSuccess,
                        margin: "2px auto 0",
                      }} />
                    );
                  }
                  return null;
                }}
              />
            </div>

            {/* Selected date hint */}
            {selectedSnapshotDate ? (
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                Selected: {dayjs(selectedSnapshotDate).format("DD MMM YYYY")}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                Click a date with green dot to view details
              </Text>
            )}
          </Col>

          {/* Right: Scorecards + Intraday 288-point Chart (75%) */}
          <Col xs={24} lg={18}>
            {/* Scorecards row above the chart */}
            {selectedSnapshotDate && selectedSnapshotData && !snapshotLoading && (
              <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
                <Col xs={12} sm={6}>
                  <Card size="small" styles={{ body: { padding: "8px 12px" } }} style={{ background: "rgba(5, 166, 119, 0.08)", textAlign: "center" }}>
                    <Statistic
                      title={<Text style={{ fontSize: 10 }}>Bat Discharge</Text>}
                      value={selectedSnapshotData.summary?.totalBatDischarge || 0}
                      precision={1}
                      suffix="kWh"
                      valueStyle={{ fontSize: 15, color: token.colorSuccess }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" styles={{ body: { padding: "8px 12px" } }} style={{ background: "rgba(250, 82, 82, 0.08)", textAlign: "center" }}>
                    <Statistic
                      title={<Text style={{ fontSize: 10 }}>Bat Charge</Text>}
                      value={selectedSnapshotData.summary?.totalBatCharge || 0}
                      precision={1}
                      suffix="kWh"
                      valueStyle={{ fontSize: 15, color: token.colorError }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" styles={{ body: { padding: "8px 12px" } }} style={{ background: "rgba(250, 173, 20, 0.08)", textAlign: "center" }}>
                    <Statistic
                      title={<Text style={{ fontSize: 10 }}>PV Energy</Text>}
                      value={selectedSnapshotData.summary?.totalEpv || 0}
                      precision={1}
                      suffix="kWh"
                      valueStyle={{ fontSize: 15, color: "#faad14" }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" styles={{ body: { padding: "8px 12px" } }} style={{ background: "rgba(84, 162, 204, 0.08)", textAlign: "center" }}>
                    <Statistic
                      title={<Text style={{ fontSize: 10 }}>Systems</Text>}
                      value={selectedSnapshotData.summary?.systemCount || 0}
                      valueStyle={{ fontSize: 15, color: token.colorPrimary }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {!selectedSnapshotDate ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 280 }}>
                <Empty description="Select a date from the calendar to view intraday data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : snapshotLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 280 }}>
                <Spin size="large" />
              </div>
            ) : !selectedSnapshotData?.chartData?.length ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 280 }}>
                <Empty description="No intraday data available for this date" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : (
              <Tabs
                activeKey={snapshotHistoryTab}
                onChange={setSnapshotHistoryTab}
                size="small"
                items={[
                  {
                    key: "power",
                    label: "Power (kW)",
                    children: (
                      <div style={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={selectedSnapshotData.chartData.map((d, i) => ({
                              ...d,
                              time: dayjs(d.ts).tz(AEST_TIMEZONE).format("HH:mm"),
                              idx: i,
                            }))}
                            margin={{ top: 10, right: 10, bottom: 5, left: 0 }}
                          >
                            <defs>
                              <linearGradient id="pvGradSnap" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#faad14" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#faad14" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="loadGradSnap" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                            <XAxis
                              dataKey="time"
                              tick={{ fontSize: 10 }}
                              interval={23}
                            />
                            <YAxis tick={{ fontSize: 10 }} />
                            <RechartsTooltip
                              formatter={(value, name) => [value != null ? `${value.toFixed(2)} kW` : "N/A", name]}
                              labelFormatter={(v) => v}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="pv" name="PV" stroke="#faad14" fill="url(#pvGradSnap)" connectNulls={false} />
                            <Area type="monotone" dataKey="load" name="Load" stroke={token.colorPrimary} fill="url(#loadGradSnap)" connectNulls={false} />
                            <Area type="monotone" dataKey="battery" name="Battery" stroke="#52c41a" fill="none" strokeWidth={2} connectNulls={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ),
                  },
                  {
                    key: "grid",
                    label: "Grid (kW)",
                    children: (
                      <div style={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={selectedSnapshotData.chartData.map((d) => ({
                              ...d,
                              time: dayjs(d.ts).tz(AEST_TIMEZONE).format("HH:mm"),
                            }))}
                            margin={{ top: 10, right: 10, bottom: 5, left: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={23} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <RechartsTooltip
                              formatter={(value, name) => [value != null ? `${value.toFixed(2)} kW` : "N/A", name]}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="grid" name="Grid Power" stroke={token.colorPrimary} fill="none" connectNulls={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ),
                  },
                  {
                    key: "headroom",
                    label: "Headroom/Footroom",
                    children: (
                      <div style={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={selectedSnapshotData.chartData.map((d) => ({
                              ...d,
                              time: dayjs(d.ts).tz(AEST_TIMEZONE).format("HH:mm"),
                            }))}
                            margin={{ top: 10, right: 10, bottom: 5, left: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={23} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <RechartsTooltip
                              formatter={(value, name) => [value != null ? `${value.toFixed(2)} kW` : "N/A", name]}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="headroom" name="Headroom" stroke={token.colorSuccess} fill="none" connectNulls={false} />
                            <Area type="monotone" dataKey="footroom" name="Footroom" stroke={token.colorError} fill="none" connectNulls={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ),
                  },
                  {
                    key: "online",
                    label: "Online Count",
                    children: (
                      <div style={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={selectedSnapshotData.chartData.map((d) => ({
                              ...d,
                              time: dayjs(d.ts).tz(AEST_TIMEZONE).format("HH:mm"),
                            }))}
                            margin={{ top: 10, right: 10, bottom: 5, left: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={23} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <RechartsTooltip />
                            <Legend />
                            <Line type="stepAfter" dataKey="onlineCount" name="Online Systems" stroke={token.colorSuccess} strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </Col>
        </Row>
      </Card>

      {/* Tabs */}
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ marginTop: -8 }}
        />
      </Card>

      {/* Emergency Stop Modal */}
      <Modal
        title={
          <Space>
            <StopOutlined style={{ color: "#ff4d4f" }} />
            <span style={{ color: "#ff4d4f" }}>Emergency Stop</span>
          </Space>
        }
        open={emergencyStopModalVisible}
        onCancel={() => {
          setEmergencyStopModalVisible(false);
          setEmergencyStopReason("");
        }}
        footer={[
          <Button key="cancel" onClick={() => setEmergencyStopModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="stop"
            type="primary"
            danger
            icon={<StopOutlined />}
            loading={emergencyStopLoading}
            onClick={handleEmergencyStop}
          >
            Confirm Emergency Stop
          </Button>,
        ]}
        width={500}
      >
        <Alert
          message="Warning: This action has the highest priority"
          description={
            <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
              <li>All active dispatches will be immediately cancelled</li>
              <li>All scheduled dispatches will be suspended</li>
              <li>This overrides ALL rules including Compliance Lock</li>
              <li>Systems will return to idle state</li>
            </ul>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginBottom: 8 }}>
          <Text strong>Reason for Emergency Stop <Text type="danger">*</Text></Text>
        </div>
        <Input.TextArea
          rows={3}
          value={emergencyStopReason}
          onChange={(e) => setEmergencyStopReason(e.target.value)}
          placeholder="Please provide a reason for activating emergency stop (e.g., safety concern, equipment issue, grid emergency)..."
        />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Priority Hierarchy: Emergency Stop &gt; Compliance Lock &gt; Preemptive Rules &gt; Normal Priority
          </Text>
        </div>
      </Modal>
    </PageLayout>
  );
}
